import { dataLayerPush, getBaseUrlJoined, layoutEvents, trackMainNavLinkClick } from '@libs/analytics';
import { Icon, TextLink } from '@petsmart-ui/sparky';
import { NAVIGATION_URLS } from '@libs/core';
import { propsImagesItems } from '../../utils/propsImagesItems';
import Banner, { type BannerProps } from '../Banner';
import type { Card } from '../CardUp';
import PromoCard from '../PromoCard';
import type { MarketingSectionProps } from './types';
import { SPECIAL_TYPES } from './types';

import type { BannerVariant, CTA, Link as LinkType, TextAlignment, TextColor } from '../../types';

type MenuBannerProps = {
    rightMenuCard: BannerProps;
    onClick: () => void;
};

function MenuBanner({ rightMenuCard, onClick }: MenuBannerProps) {
    const { variant, header, description, textColor, textAlignment, image, cta } = rightMenuCard;
    return (
        <Banner
            data-testid="category-marketing-card"
            onClick={onClick}
            variant={('variant' in rightMenuCard && variant) as BannerVariant}
            header={header || ''}
            description={description || ''}
            textColor={('textColor' in rightMenuCard && textColor) as TextColor}
            textAlignment={('textAlignment' in rightMenuCard && textAlignment) as TextAlignment}
            image={image}
            cta={'cta' in rightMenuCard && cta ? (cta as CTA) : undefined}
        />
    );
}

type MenuPromoCardProps = {
    rightMenuCard: Card;
    onClick?: () => void;
};

export function MenuPromoCard({ rightMenuCard, onClick }: MenuPromoCardProps) {
    const { link, header, description, image } = rightMenuCard;
    return (
        <PromoCard
            data-testid="category-marketing-card"
            description={description}
            onClick={onClick}
            link={'link' in rightMenuCard && link ? (link as LinkType) : undefined}
            imgAlt={image.webAltText}
            imageItems={propsImagesItems(image)}
            header={'header' in rightMenuCard && header ? header : ''}
        >
            {'link' in rightMenuCard && link && (
                <p className="promo-card__link">
                    Shop now
                    <Icon name="chevron-right" size="sm" />
                </p>
            )}
        </PromoCard>
    );
}

export const trackingData = (data: object) => Object.assign(data, { ...layoutEvents.headerMenuNavClick });

export function MarketingSection({ rightMenuCard, childCategories, specialType, path }: MarketingSectionProps) {
    const shopAllLink = childCategories?.find(({ shopAllCategory }) => shopAllCategory);
    const { displayText, link } = shopAllLink || {};

    const cardClick = (href?: string | undefined) => {
        if (rightMenuCard) {
            const categoryPath = [...path, rightMenuCard.header].join(' | ');
            trackMainNavLinkClick(categoryPath);

            dataLayerPush(
                trackingData({
                    selection: categoryPath,
                    link_url: getBaseUrlJoined(href),
                })
            );
        }
    };

    const trackClick = (newPath: string, href: string | undefined) => () => {
        const categoryPath = [...path, newPath].join(' | ');
        trackMainNavLinkClick(categoryPath);

        dataLayerPush(
            trackingData({
                selection: categoryPath,
                link_url: getBaseUrlJoined(href),
            })
        );
    };

    const renderedComponent = (type: string | undefined): React.ReactNode =>
        type === 'card' ? (
            <MenuPromoCard
                rightMenuCard={rightMenuCard as Card}
                onClick={() => cardClick((rightMenuCard as Card)?.link?.webUrl)}
            />
        ) : type === 'banner' ? (
            <MenuBanner
                rightMenuCard={rightMenuCard as BannerProps}
                onClick={() => cardClick((rightMenuCard as BannerProps)?.cta?.link?.webUrl)}
            />
        ) : null;

    const shopAllBrands = 'Shop all Brands';
    const shopByBrandHref = NAVIGATION_URLS.legacy.shopByBrand;

    return specialType === SPECIAL_TYPES.SHOP_BY_BRAND ? (
        <div className="submenu-container__data">
            <div id="all-brands-link">
                <a href={shopByBrandHref} onClick={trackClick(shopAllBrands, shopByBrandHref)}>
                    <p>{shopAllBrands}</p>
                </a>
            </div>
        </div>
    ) : (
        <div className="submenu-container__data">
            {renderedComponent(rightMenuCard?.sysType)}
            {shopAllLink && displayText && (
                <TextLink
                    href={link}
                    onClick={trackClick(displayText, link)}
                    variant="secondary"
                    className="category__link--shopall"
                    data-testid="category-shopall"
                >
                    {displayText}
                    <Icon name="chevron-right" size="md" />
                </TextLink>
            )}
        </div>
    );
}
